/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Sebastian Hartmann <s.hartmann@techdivision.com>
 */
import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Paper,
} from '@mui/material';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useSelectedStudio } from './useSelectedStudio';
import { OpeningHour } from './OpeningHour';
import './styles.scss';

/**
 * SelectedStudio Component
 * @returns {Element}
 * @constructor
 * @param props
 */
export function SelectedStudio(props) {
  const { studio, className, showToStudioLink } = props;
  const { handleCloseStudioDetails, handleLinkToStudio, buttonDisabled } = useSelectedStudio(props);
  const { t } = useTranslation();

  /**
     * Render the studio details
     * @returns {JSX.Element}
     */
  return (
    <Paper elevation={3} className={`selected-studio ${className}`}>
      {studio.studioName ? (
        <p className="studio-headline">
          <span>{studio.studioName}</span>
        </p>
      ) : (
        <p className="studio-headline">{t('startNowGym')}</p>
      )}
      <IconButton
        className="studio-close"
        sx={{ p: '10px' }}
        aria-label="close"
        onClick={handleCloseStudioDetails}
      >
        <CloseIcon />
      </IconButton>
      <Box className="studio-infos">
        <Box className="studio-info">
          <Box className="studio-info">
            <Box>
              {studio.address.street ? (
                <span>
                  {studio.address.street}
                  {' '}
                </span>
              ) : ''}
              {studio.address.houseNumber ? <span>{studio.address.houseNumber}</span> : ''}
            </Box>
            <Box>
              {studio.address.zipCode ? (
                <span>
                  {studio.address.zipCode}
                  {' '}
                </span>
              ) : ''}
              {studio.address.city ? <span>{studio.address.city}</span> : ''}
            </Box>
          </Box>
          {studio.openingHours ? (
            <Accordion className="studio-opening">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                {t('openingHours')}
              </AccordionSummary>
              <AccordionDetails>
                {studio.openingHours.map((openingHour) => (
                  <OpeningHour
                    key={openingHour.dayOfWeekFrom}
                    day={openingHour.dayOfWeekFrom}
                    from={openingHour.timeFrom}
                    to={openingHour.timeTo}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ) : (
            ''
          )}
        </Box>
        {studio.studioPhone ? (
          <Box className="studio-info">
            <span>
              {t('telephoneForStudio')}
              <a className="highlight" href={`tel:${studio.studioPhone}`}>
                {studio.studioPhone}
              </a>
            </span>
          </Box>
        ) : (
          ''
        )}
        {showToStudioLink ? (
          <Box className="button-container">
            <Button
              className="checkout-button"
              sx={{ mb: '10px' }}
              variant="contained"
              disabled={buttonDisabled}
              onClick={handleLinkToStudio}
            >
              Zum Studio
            </Button>
          </Box>
        ) : (
          ''
        )}
      </Box>
    </Paper>
  );
}

SelectedStudio.propTypes = {
  studio: PropTypes.object.isRequired,
  className: PropTypes.string,
  showToStudioLink: PropTypes.bool,
};

SelectedStudio.defaultProps = {
  className: null,
  showToStudioLink: true,
};
